/**
 * User and authentication-related TypeScript types
 */

/**
 * User profile data model - contains all user information stored in Firestore
 */
export interface UserProfile {
  // Basic Fields (Already Implemented)
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  subscriptionStatus: 'active' | 'inactive' | 'trial' | 'past_due';
  createdAt: string;

  // Name fields
  firstName?: string;
  lastName?: string;

  // Enhanced Fields
  preferredName?: string;
  contactEmail?: string; // May differ from auth email
  phoneNumber?: string;
  location?: {
    country?: string;
    region?: string;
    city?: string;
    postalCode?: string;
  };
  timezone?: string;
  preferences?: {
    theme?: 'light' | 'dark' | 'system';
    emailNotifications?: boolean;
    newsletterSubscribed?: boolean;
    contentPreferences?: string[];
  };
  profileCompleteness?: number; // Percentage of profile completed
  lastActive?: string; // ISO timestamp

  // Subscription Details
  subscriptionDetails?: {
    plan?: string;
    startDate?: string;
    renewalDate?: string;
    paymentMethod?: string;
    status?: string;
  };

  // Metadata
  authProvider?: 'email' | 'google' | 'facebook';
  userRole?: 'user' | 'admin' | 'moderator';
  isEmailVerified?: boolean;
}

/**
 * Custom claims stored in Firebase JWT token
 */
export interface UserClaims {
  subscribed?: boolean;
  role?: 'admin' | 'user' | 'moderator';
  plan?: string;
  expiresAt?: number;
}

/**
 * Subset of UserProfile with only fields that can be updated directly by a user
 */
export type UserProfileUpdateData = Omit<
  Partial<UserProfile>,
  'uid' | 'email' | 'createdAt' | 'subscriptionStatus' | 'subscriptionDetails' | 'userRole' | 'authProvider' | 'isEmailVerified' | 'profileCompleteness'
>;

/**
 * Authentication error types for categorization
 */
export enum AuthErrorType {
  CREDENTIALS = 'credentials',
  NETWORK = 'network',
  ACCOUNT = 'account',
  VERIFICATION = 'verification',
  PERMISSIONS = 'permissions',
  UNKNOWN = 'unknown'
}

/**
 * Authentication error with additional context
 */
export interface AuthError {
  code: string;
  message: string;
  type: AuthErrorType;
  action?: string; // Recommended action to resolve the error
}
